/**
 * La puerta del panel para escribir un envío (plan «un paquete, una fila de envío», F2c, 27-sep).
 *
 * 🚨 POR QUÉ EXISTE. El panel escribía el envío con `update`/`insert` directos, acotados por la cuenta del
 *    pedido que se abrió. En Smarty Drop el paquete es UNO y vive en la venta (la fila de los cuatro): el
 *    almacén que registra la guía desde el encargo escribe una fila de OTRA cuenta, y la RLS no se lo deja,
 *    como tiene que ser. `fn_envio_escribir` decide en la base: el dueño de la fila, o quien tiene parte de
 *    almacén o de courier en el pedido.
 *
 * ⭐ LO QUE HACE LA BASE Y NO EL NAVEGADOR:
 *    · qué fila se escribe (la del paquete que corresponde: `fn_envio_a_escribir`);
 *    · qué columnas se pueden tocar (las del envío; nunca el pedido ni la cuenta);
 *    · el evento del historial se AGREGA en la base: antes el navegador leía los eventos, sumaba uno y
 *      escribía la lista entera, y dos escrituras seguidas se pisaban.
 *
 * Para el dueño de la fila hace lo mismo que la escritura directa de antes.
 */
import { supabase } from '@/lib/supabase';

/** Las columnas del envío que escribe el panel. `undefined` = no se toca; `null` = se borra. */
export interface CambiosDelEnvio {
  carrier?: string | null;
  tracking_number?: string | null;
  status?: string;
  status_source?: string | null;
  agency_ref?: string | null;
  agency_name?: string | null;
  secret_code?: string | null;
  package_size?: string | null;
  delivery_mode?: string | null;
  last_tracking?: unknown;
  last_tracked_at?: string | null;
  rastreo_imagen_url?: string | null;
  rastreo_imagen_version?: string | null;
  motivo_terminal?: string | null;
  motivo_detalle?: string | null;
  motivo_evidencia_url?: string | null;
}

export interface EventoDelEnvio {
  status: string;
  at: string;
  note?: string | null;
  source?: string;
}

/**
 * Escribe el envío de un pedido (o el envío nombrado) y devuelve su id; `null` si no había envío y no se
 * pidió crearlo («sin envío todavía no hay dónde guardar»).
 */
export async function escribirEnvio(
  orderId: string | null,
  cambios: CambiosDelEnvio,
  opciones: { evento?: EventoDelEnvio; envioId?: string; crear?: boolean } = {},
): Promise<string | null> {
  // JSON sin los `undefined`: lo que no se pasa, no se toca.
  const soloLoPasado = Object.fromEntries(Object.entries(cambios).filter(([, v]) => v !== undefined));
  const { data, error } = await supabase.rpc('fn_envio_escribir', {
    p_order: orderId,
    p_cambios: soloLoPasado,
    p_evento: opciones.evento ?? null,
    p_envio: opciones.envioId ?? null,
    p_crear: opciones.crear ?? false,
  });
  if (error) throw error;
  return (data as string | null) ?? null;
}
